import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, Minus, CircleDot, HelpCircle, ArrowUpDown, FileText, ExternalLink,
  Loader2, ShieldQuestion, UserPlus, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";

const STROKE = 1.5;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATE_META = {
  evidence_found: { icon: Check, cls: "text-emerald-700", label: "Evidence found" },
  partial: { icon: CircleDot, cls: "text-amber-600", label: "Partial" },
  not_mentioned: { icon: Minus, cls: "text-ink-300", label: "Not mentioned" },
  needs_match: { icon: HelpCircle, cls: "text-ink-300", label: "Not yet matched" },
};

function StateIcon({ state, needs }) {
  const meta = needs ? STATE_META.needs_match : (STATE_META[state] || STATE_META.not_mentioned);
  const Icon = meta.icon;
  return <Icon className={`h-4 w-4 ${meta.cls}`} strokeWidth={STROKE} aria-label={meta.label} />;
}

function MatchCell({ row }) {
  if (row.display_mode === "percent" && row.match_rate != null) {
    return (
      <span className="font-medium text-ink-900" data-testid="match-value">
        {row.match_rate}% <span className="text-ink-400">· {row.met_count} of {row.total_count}</span>
      </span>
    );
  }
  if (row.display_mode === "fraction") {
    return (
      <span className="text-ink-700" title="Add more requirements for a meaningful match rate" data-testid="match-value">
        {row.met_count} of {row.total_count} met
        <span className="ml-1 text-amber-600">*</span>
      </span>
    );
  }
  return <span className="text-ink-400" data-testid="match-value">—</span>;
}

function EffectiveRate(row) {
  if (row.match_rate != null) return row.match_rate;
  if (row.total_count > 0) return Math.round((row.met_count / row.total_count) * 100);
  return -1;
}

function EvidenceDrawer({ cvId, requirements, canEdit, onClose, onOverridden }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/screening-cvs/${cvId}/matches`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cvId]);

  const override = (match) => async (state) => {
    if (!match?.requirement_match_id) {
      toast.error("This requirement isn't matched yet — run screening first.");
      return;
    }
    setBusy(match.jd_requirement_id);
    try {
      await api.patch(`/requirement-matches/${match.requirement_match_id}`, { state });
      await load();
      onOverridden();
      toast.success("Match updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="evidence-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[15px]">
            <FileText className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
            {data?.cv?.original_filename || "CV"}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-2 flex items-center gap-3">
          <a
            href={`${API_BASE}/api/screening-cvs/${cvId}/download`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[12.5px] text-emerald-800 hover:underline"
            data-testid="drawer-view-original"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={STROKE} /> View original CV
          </a>
        </div>
        <div className="mt-4 rounded-md border border-ink-200 bg-ink-50/50 px-3 py-2 text-[11.5px] text-ink-500">
          Evidence is quoted verbatim from the CV. This is a requirement-coverage check, not a
          candidate assessment.
        </div>
        {!data ? (
          <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div>
        ) : (
          <div className="mt-4 space-y-3">
            {data.matches.map((m) => (
              <div key={m.jd_requirement_id} className="rounded-md border border-ink-100 p-3" data-testid={`evidence-${m.jd_requirement_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <StateIcon state={m.state} needs={m.needs_match} />
                      <span className="text-[13px] font-medium text-ink-900">{m.label}</span>
                      {m.weight === "must_have" && (
                        <span className="rounded bg-emerald-800/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">must</span>
                      )}
                      {m.is_overridden && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700" data-testid="overridden-badge">adjusted</span>
                      )}
                    </div>
                    {m.evidence_text ? (
                      <blockquote className="mt-2 border-l-2 border-emerald-800/30 pl-3 text-[12.5px] italic leading-relaxed text-ink-600" data-testid="evidence-quote">
                        &ldquo;{m.evidence_text}&rdquo;
                      </blockquote>
                    ) : (
                      <p className="mt-2 text-[12px] text-ink-400">
                        {m.needs_match ? "Not yet matched." : "No supporting evidence found in the CV."}
                      </p>
                    )}
                  </div>
                  {canEdit && !m.needs_match && (
                    <div className="w-[150px] shrink-0">
                      <Select value={m.state} onValueChange={(v) => override(m)(v)} disabled={busy === m.jd_requirement_id}>
                        <SelectTrigger className="h-8 text-[12px]" data-testid={`override-${m.jd_requirement_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="evidence_found" className="text-[12px]">Evidence found</SelectItem>
                          <SelectItem value="partial" className="text-[12px]">Partial</SelectItem>
                          <SelectItem value="not_mentioned" className="text-[12px]">Not mentioned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function ResultsTable({ results, canEdit, onRefresh, onPromote }) {
  const [sortKey, setSortKey] = useState("match");
  const [minMatch, setMinMatch] = useState(0);
  const [mustOnly, setMustOnly] = useState(false);
  const [openCv, setOpenCv] = useState(null);

  const reqs = results.requirements || [];
  const rows = useMemo(() => {
    let r = [...(results.rows || [])];
    // A CV that hasn't been matched yet has no rate, so it can't be filtered by
    // one — it stays visible (as "Not yet matched") until it has been screened.
    const unmatched = (x) => x.match_rate === null || x.match_rate === undefined;
    r = r.filter((x) => unmatched(x) || EffectiveRate(x) >= minMatch);
    if (mustOnly) r = r.filter((x) => x.must_have_total > 0 && x.must_have_met_count === x.must_have_total);
    r.sort((a, b) => {
      if (unmatched(a) !== unmatched(b)) return unmatched(a) ? 1 : -1;
      return sortKey === "must"
        ? (b.must_have_met_count - a.must_have_met_count)
        : (EffectiveRate(b) - EffectiveRate(a));
    });
    return r;
  }, [results.rows, minMatch, mustOnly, sortKey]);

  return (
    <div data-testid="results-table">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="hp-section-label">Requirement match · highest coverage first</span>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[12px]">
          <label className="flex items-center gap-1.5 text-ink-500">
            Min match
            <input
              type="number" min={0} max={100} value={minMatch}
              onChange={(e) => setMinMatch(Number(e.target.value) || 0)}
              className="w-16 rounded-md border border-ink-200 px-2 py-1"
              data-testid="filter-min-match"
            />%
          </label>
          <label className="flex items-center gap-1.5 text-ink-500" data-testid="filter-must-only">
            <input type="checkbox" checked={mustOnly} onChange={(e) => setMustOnly(e.target.checked)} />
            All must-haves met
          </label>
          <button onClick={() => setSortKey(sortKey === "match" ? "must" : "match")}
            className="inline-flex items-center gap-1 rounded-md border border-ink-200 px-2 py-1 text-ink-600 hover:border-emerald-800/40"
            data-testid="toggle-sort">
            <ArrowUpDown className="h-3 w-3" strokeWidth={STROKE} />
            {sortKey === "match" ? "Match" : "Must-haves"}
          </button>
        </div>
      </div>

      <div className="hp-card overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="border-b border-ink-200 text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-3 py-2 font-medium">CV</th>
              <th className="px-3 py-2 font-medium">Match</th>
              <th className="px-3 py-2 font-medium">Must-haves</th>
              {reqs.map((r) => (
                <th key={r.jd_requirement_id} className="px-2 py-2 text-center font-medium" title={r.label}>
                  <span className="block max-w-[64px] truncate">{r.label}</span>
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4 + reqs.length} className="p-8 text-center text-[13px] text-ink-400" data-testid="results-empty">No CVs match the current filters.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.screening_cv_id} className="border-b border-ink-100 hover:bg-ink-50/40" data-testid={`result-row-${row.screening_cv_id}`}>
                <td className="px-3 py-2.5">
                  <button onClick={() => setOpenCv(row.screening_cv_id)} className="text-left text-[13px] font-medium text-ink-900 hover:text-emerald-800" data-testid={`open-cv-${row.screening_cv_id}`}>
                    {row.candidate_name || row.original_filename}
                  </button>
                  {row.requirements_needing_match > 0 && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">{row.requirements_needing_match} unmatched</span>
                  )}
                </td>
                <td className="cursor-pointer px-3 py-2.5 text-[13px]" onClick={() => setOpenCv(row.screening_cv_id)}>
                  <MatchCell row={row} />
                </td>
                <td className="px-3 py-2.5 text-[13px] text-ink-700">{row.must_have_met_count} of {row.must_have_total}</td>
                {reqs.map((r) => {
                  const s = row.states?.[r.jd_requirement_id];
                  return (
                    <td key={r.jd_requirement_id} className="px-2 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center" title={s ? s.state : "not matched"}>
                        <StateIcon state={s?.state} needs={!s} />
                      </span>
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button onClick={() => setOpenCv(row.screening_cv_id)} className="inline-flex items-center gap-1 text-[12px] text-emerald-800 hover:underline" data-testid={`view-evidence-${row.screening_cv_id}`}>
                      <ShieldQuestion className="h-3.5 w-3.5" strokeWidth={STROKE} /> Evidence
                    </button>
                    {row.candidate_id ? (
                      <Link to={`/candidates/${row.candidate_id}`} className="inline-flex items-center gap-1 text-[12px] text-ink-500 hover:text-emerald-800" data-testid={`in-pipeline-${row.screening_cv_id}`}>
                        <UserCheck className="h-3.5 w-3.5" strokeWidth={STROKE} /> In pipeline
                      </Link>
                    ) : canEdit && onPromote && (
                      <button onClick={() => onPromote(row)} className="inline-flex items-center gap-1 text-[12px] text-ink-500 hover:text-emerald-800" data-testid={`promote-${row.screening_cv_id}`}>
                        <UserPlus className="h-3.5 w-3.5" strokeWidth={STROKE} /> Add to pipeline
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {results.failed?.length > 0 && (
        <div className="mt-3 rounded-md border border-ink-200 bg-ink-50/50 p-3" data-testid="results-failed">
          <div className="text-[12.5px] font-medium text-ink-700">Could not read ({results.failed.length})</div>
          <ul className="mt-1.5 space-y-0.5 text-[12px] text-ink-500">
            {results.failed.map((f) => (
              <li key={f.screening_cv_id} className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-ink-300" strokeWidth={STROKE} />
                {f.original_filename}
                <span className="text-ink-400">· {f.extraction_status.replace("failed_", "")}{f.extraction_status === "failed_unsupported" ? " / not searchable" : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {openCv && (
        <EvidenceDrawer
          cvId={openCv}
          requirements={reqs}
          canEdit={canEdit}
          onClose={() => setOpenCv(null)}
          onOverridden={onRefresh}
        />
      )}
    </div>
  );
}
